import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CreditCard, Landmark, Store, CheckCircle2, Loader2, HandCoins, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PaymentPanel({ quote, amount }) {
  const { t } = useTranslation();
  const pay = quote.quote?.payment || {};
  const [opts, setOpts] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/payment/options").then(({ data }) => setOpts(data)).catch(() => setOpts({}));
  }, []);

  if (!pay.required) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="payment-credit-note">
        <div className="flex items-center gap-2 text-slate-800">
          <HandCoins className="h-5 w-5 text-blue-600" />
          <h2 className="font-display text-base font-semibold">{t("pay.creditTitle")}</h2>
        </div>
        <p className="mt-2 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-sm font-medium text-blue-800">
          <HandCoins className="h-4 w-4" /> {t("pay.credit")}
        </p>
      </div>
    );
  }

  if (pay.status === "paid") {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="payment-paid-note">
        <div className="flex items-center gap-2 text-emerald-800">
          <CheckCircle2 className="h-5 w-5" />
          <h2 className="font-display text-base font-semibold">{t("pay.paid")}</h2>
        </div>
        <p className="mt-1 text-sm text-emerald-700">{t("pay.paidDesc", { amount: Number(amount).toFixed(2) })}</p>
      </div>
    );
  }

  const startCardPayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success(t("pay.copied")); };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-6" data-testid="payment-panel">
      <div className="flex items-center gap-2 text-amber-900">
        <CreditCard className="h-5 w-5" />
        <h2 className="font-display text-base font-semibold">{t("pay.title")}</h2>
      </div>
      <p className="mt-1 text-sm text-amber-800">{t("pay.requiredDesc", { amount: Number(amount).toFixed(2) })}</p>

      {!opts ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("pay.loadingOpts")}</div>
      ) : (
        <div className="mt-4 space-y-3">
          {opts.card_enabled && (
            <Button onClick={startCardPayment} disabled={paying} data-testid="pay-card-btn"
              className="h-12 w-full bg-blue-600 text-base hover:bg-blue-700">
              {paying ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CreditCard className="mr-2 h-5 w-5" />}
              {paying ? t("pay.redirecting") : t("pay.payCard")}
            </Button>
          )}

          {opts.transfer_enabled && (
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="pay-transfer-block">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Landmark className="h-4 w-4 text-blue-600" /> {t("pay.transfer")}</p>
              <div className="mt-2 space-y-1.5 text-sm text-slate-600">
                {opts.account_holder && <p>{t("pay.beneficiary")} : <span className="font-medium text-slate-800">{opts.account_holder}</span></p>}
                {opts.bank_name && <p>{t("pay.bank")} : <span className="font-medium text-slate-800">{opts.bank_name}</span></p>}
                {opts.iban && (
                  <p className="flex items-center gap-2">IBAN :
                    <span className="font-mono font-medium text-slate-800">{opts.iban}</span>
                    <button type="button" onClick={() => copy(opts.iban)} className="text-blue-600 hover:text-blue-700"><Copy className="h-3.5 w-3.5" /></button>
                  </p>
                )}
                <p>{t("pay.communication")} : <span className="font-mono font-semibold text-slate-800">{quote.ref || quote.id?.slice(-6)}</span></p>
                {opts.instructions && <p className="text-xs text-slate-500">{opts.instructions}</p>}
              </div>
            </div>
          )}

          {opts.counter_enabled && (
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="pay-counter-block">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Store className="h-4 w-4 text-blue-600" /> {t("pay.counter")}</p>
              <p className="mt-1 text-sm text-slate-600">{t("pay.counterDesc")}</p>
            </div>
          )}

          {!opts.card_enabled && !opts.transfer_enabled && !opts.counter_enabled && (
            <p className="rounded-lg bg-white px-3 py-2.5 text-sm text-slate-500">{t("pay.contactUs")}</p>
          )}
        </div>
      )}
    </div>
  );
}
